import type {
  AlignmentType,
  CapitalizationStyle,
  EditEntries,
  EditEntry,
  FieldsInJSON,
  GlobalStylesMap,
  ListDefinition,
  ListLevel,
  PageDimensions,
  ParagraphStyleVariant,
  RenderModelDocument,
  SelBoundaryNode,
  Shade,
  TableBorderSize,
  TableCellElement,
  TableElement,
  WithNullable,
} from '@aline/common';
import {
  defaultPageDimensions,
  defaultStyles,
  FooterType,
  HeaderType,
  isSetSelectionOperation,
  isTableElement,
} from '@aline/common';
import { equals, uniq } from 'ramda';
import type { NodeEntry } from 'slate';
import { Editor } from 'slate';
import { Queries } from 'src/editor/queries';

export interface FontProperties {
  bold: boolean;
  fontColor: string;
  fontHighlight: string | null;
  fontSize: string;
  fontType: string;
  italic: boolean;
  smallCaps: boolean;
  strikethrough: boolean;
  subscript: boolean;
  superscript: boolean;
  underline: boolean;
}

export interface DocumentProperties {
  pageDimensions: PageDimensions;
  styleSheet: GlobalStylesMap;
}

export interface HeaderFooterProperties {
  diffFirstPage: boolean;
  evenOdd: boolean;
}

export interface IndentationProperties {
  firstLine: number;
  left: number;
  right: number;
}
export type IndentationMarkerOffsets = Omit<IndentationProperties, 'firstLine'>;

export interface ListProperties {
  listDefinition: ListDefinition | null;
}

export interface ParagraphProperties {
  alignment: AlignmentType | '';
  indentationFirstLine: number | null;
  indentationLeft: number | null;
  indentationRight: number | null;
  lineSpacing: number | null;
  listLevel: ListLevel | null;
  shade: Shade | null;
  spacingAfter: number | null;
  spacingBefore: number | null;
  styleVariant: ParagraphStyleVariant | '';
}

export interface DerivedTableProperties extends WithNullable<
  Pick<TableElement, 'align' | 'indent' | 'shade'>
> {
  activeBorderColor: string | null;
  activeBorderWeight: TableBorderSize | null;
}

export type DerivedTableCellProperties = WithNullable<Pick<TableCellElement, 'shade'>>;

export interface EditorDerivedState {
  activeCapitalizationStyle: CapitalizationStyle | null;
  activeEdits: EditEntry[] | null;
  commentNodeIds: UUID[];
  documentProperties: DocumentProperties;
  fieldsInJSON: FieldsInJSON;
  fontProperties: FontProperties;
  headerFooterProperties: HeaderFooterProperties;
  indentationMarkerOffsets: IndentationMarkerOffsets;
  indentationProperties: IndentationProperties;
  listProperties: ListProperties;
  paragraphProperties: ParagraphProperties;
  selBoundaryEntry: NodeEntry<SelBoundaryNode> | null;
  suggestedEdits: EditEntries;
  tableCellProperties: DerivedTableCellProperties;
  tableProperties: DerivedTableProperties;
}

export interface EditorDocumentUiState {
  footerOpen: boolean;
  headerOpen: boolean;
}

export interface EditorDerivedSideEffects {
  documentUi: Partial<EditorDocumentUiState>;
  selectedCommentIds: UUID[];
}

export const initialEditorDerivedState: EditorDerivedState = {
  activeCapitalizationStyle: null,
  activeEdits: null,
  commentNodeIds: [],
  documentProperties: {
    pageDimensions: defaultPageDimensions,
    styleSheet: defaultStyles,
  },
  fieldsInJSON: { cannedInChildren: {}, inChildren: {}, inProps: {} },
  fontProperties: {
    bold: false,
    fontColor: '',
    fontHighlight: 'transparent',
    fontSize: '',
    fontType: '',
    italic: false,
    smallCaps: false,
    strikethrough: false,
    subscript: false,
    superscript: false,
    underline: false,
  },
  headerFooterProperties: {
    diffFirstPage: false,
    evenOdd: false,
  },
  indentationMarkerOffsets: { left: 0, right: 0 },
  indentationProperties: { firstLine: 0, left: 0, right: 0 },
  listProperties: {
    listDefinition: null,
  },
  paragraphProperties: {
    alignment: '',
    indentationFirstLine: null,
    indentationLeft: null,
    indentationRight: null,
    lineSpacing: null,
    listLevel: null,
    shade: null,
    spacingAfter: null,
    spacingBefore: null,
    styleVariant: '',
  },
  selBoundaryEntry: null,
  suggestedEdits: [],
  tableCellProperties: {
    shade: null,
  },
  tableProperties: {
    activeBorderColor: null,
    activeBorderWeight: null,
    align: null,
    indent: null,
    shade: null,
  },
};

export const fieldDetailsArgs: Parameters<typeof Queries.fieldDetails>[1] = {
  getDynamic: 'nested',
};

export function deriveSelectionDerivedState(
  editor: Editor,
  previousState: EditorDerivedState,
  documentUiState: EditorDocumentUiState
): { sideEffects: EditorDerivedSideEffects; state: EditorDerivedState } {
  const selBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor) ?? null;
  const selectedCommentIds = Queries.getSelectedCommentIds(editor);

  const stateWithSelectionProperties = deriveSelectionProperties(editor, previousState);
  const stateWithHeaderFooterProperties = editor.renderModel
    ? deriveHeaderFooterProperties(editor.renderModel, stateWithSelectionProperties)
    : stateWithSelectionProperties;
  const stateWithSelectionBoundary = setSelectionBoundary(
    stateWithHeaderFooterProperties,
    selBoundaryEntry
  );
  const stateWithDocumentProperties = setDocumentProperties(stateWithSelectionBoundary, {
    pageDimensions: Queries.activePageDimensions(editor, 'in'),
    styleSheet: Queries.activeStyleSheet(editor),
  });
  const documentUi = deriveActiveDocPartState(editor, documentUiState);
  const state = editor.operations.every(isSetSelectionOperation)
    ? setActiveEdits(
        stateWithDocumentProperties,
        Queries.activeEdits(editor, stateWithDocumentProperties.suggestedEdits)
      )
    : stateWithDocumentProperties;

  return { sideEffects: { documentUi, selectedCommentIds }, state };
}

export function deriveExpensiveEditorDerivedState(
  editor: Editor,
  previousState: EditorDerivedState
): EditorDerivedState {
  const editEntries = Queries.sortedEditRanges(editor);
  const activeEdits = Queries.activeEdits(editor, editEntries);
  const commentNodeIds = Queries.getCommentIdsInJSON(editor);
  const fieldsInJSON = Queries.fieldDetails(editor, fieldDetailsArgs);

  return setFieldsInJson(
    setCommentNodeIds(
      setActiveEdits(setSuggestedEdits(previousState, editEntries), activeEdits),
      commentNodeIds
    ),
    fieldsInJSON
  );
}

export function setSuggestedEdits(
  state: EditorDerivedState,
  suggestedEdits: EditEntries
): EditorDerivedState {
  if (equals(suggestedEdits, state.suggestedEdits)) return state;
  return { ...state, suggestedEdits };
}

export function setActiveEdits(
  state: EditorDerivedState,
  activeEdits: EditEntry[] | null
): EditorDerivedState {
  if (equals(activeEdits, state.activeEdits)) return state;
  return { ...state, activeEdits };
}

function setCommentNodeIds(state: EditorDerivedState, commentNodeIds: UUID[]): EditorDerivedState {
  const newNodeIds = uniq(commentNodeIds);
  if (equals(newNodeIds, state.commentNodeIds)) return state;
  return { ...state, commentNodeIds: newNodeIds };
}

function setDocumentProperties(
  state: EditorDerivedState,
  documentProperties: DocumentProperties
): EditorDerivedState {
  if (equals(documentProperties, state.documentProperties)) return state;
  return { ...state, documentProperties };
}

function setFieldsInJson(
  state: EditorDerivedState,
  fieldsInJSON: FieldsInJSON
): EditorDerivedState {
  if (equals(fieldsInJSON, state.fieldsInJSON)) return state;
  return { ...state, fieldsInJSON };
}

function setSelectionBoundary(
  state: EditorDerivedState,
  selBoundaryEntry: NodeEntry<SelBoundaryNode> | null
): EditorDerivedState {
  if (equals(selBoundaryEntry, state.selBoundaryEntry)) return state;
  return { ...state, selBoundaryEntry };
}

function deriveSelectionProperties(editor: Editor, previousState: EditorDerivedState) {
  const consistentFormats = editor.selection?.listMarker
    ? Queries.activeListMarkerAtLocation(editor)
    : Queries.consistentFormats(editor);
  const table = Editor.above(editor, { match: isTableElement })?.[0] ?? null;
  const listDefinition = Queries.consistentListDefinition(editor);
  const paragraphProps = Queries.consistentParagraphProps(editor);
  const tableCellBorder = Queries.getSelectedTableCellBorder(editor);
  const activeBorderWeight = tableCellBorder.size;
  const activeBorderColor = tableCellBorder.color;

  const fontProperties = createFontProperties(consistentFormats);
  const listProperties: ListProperties = {
    listDefinition,
  };
  const paragraphProperties: ParagraphProperties = {
    alignment: paragraphProps?.alignment ?? '',
    indentationFirstLine: paragraphProps?.indentationFirstLine ?? null,
    indentationLeft: paragraphProps?.indentationLeft ?? null,
    indentationRight: paragraphProps?.indentationRight ?? null,
    lineSpacing: paragraphProps?.lineSpacing ?? null,
    listLevel: paragraphProps?.listLevel ?? null,
    shade: paragraphProps?.shade ?? null,
    spacingAfter: paragraphProps?.spacingAfter ?? null,
    spacingBefore: paragraphProps?.spacingBefore ?? null,
    styleVariant: paragraphProps?.variant ?? '',
  };
  const indentationProperties = Queries.indentationProperties(editor);
  const indentationMarkerOffsets = Queries.activeIndentationMarkerOffsets(editor);
  const activeCapitalizationStyle = Queries.activeCapitalizationStyle(editor) ?? null;

  const tableProperties: DerivedTableProperties = {
    activeBorderColor,
    activeBorderWeight,
    align: table && Queries.renderedTableAlign(table),
    indent: table ? { type: 'inch', value: Queries.renderedTableIndent(table, 'in') } : null,
    shade: table && Queries.renderedTableShade(table),
  };

  const tableCellProperties: DerivedTableCellProperties = {
    shade: Queries.getSelectedTableCellShade(editor),
  };

  return {
    ...previousState,
    activeCapitalizationStyle: equals(
      activeCapitalizationStyle,
      previousState.activeCapitalizationStyle
    )
      ? previousState.activeCapitalizationStyle
      : activeCapitalizationStyle,
    fontProperties: equals(fontProperties, previousState.fontProperties)
      ? previousState.fontProperties
      : fontProperties,
    indentationMarkerOffsets: equals(
      indentationMarkerOffsets,
      previousState.indentationMarkerOffsets
    )
      ? previousState.indentationMarkerOffsets
      : indentationMarkerOffsets,
    indentationProperties: equals(indentationProperties, previousState.indentationProperties)
      ? previousState.indentationProperties
      : indentationProperties,
    listProperties: equals(listProperties, previousState.listProperties)
      ? previousState.listProperties
      : listProperties,
    paragraphProperties: equals(paragraphProperties, previousState.paragraphProperties)
      ? previousState.paragraphProperties
      : paragraphProperties,
    tableCellProperties: equals(tableCellProperties, previousState.tableCellProperties)
      ? previousState.tableCellProperties
      : tableCellProperties,
    tableProperties: equals(tableProperties, previousState.tableProperties)
      ? previousState.tableProperties
      : tableProperties,
  };
}

function deriveHeaderFooterProperties(
  rMDocument: RenderModelDocument,
  previousState: EditorDerivedState
): EditorDerivedState {
  const activeHeaderFooterProps = Queries.activeHeaderOrFooterProps(rMDocument);
  const headerFooterProperties: HeaderFooterProperties = {
    diffFirstPage: activeHeaderFooterProps.diffFirstPage,
    evenOdd: activeHeaderFooterProps.evenOdd,
  };

  if (equals(headerFooterProperties, previousState.headerFooterProperties)) return previousState;
  return { ...previousState, headerFooterProperties };
}

function deriveActiveDocPartState(
  editor: Editor,
  documentUiState: EditorDocumentUiState
): Partial<EditorDocumentUiState> {
  const selBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor);
  if (!selBoundaryEntry) return {};

  const [selBoundaryNode] = selBoundaryEntry;
  const documentUi: Partial<EditorDocumentUiState> = {};

  switch (selBoundaryNode.type) {
    case 'document': {
      if (documentUiState.footerOpen) documentUi.footerOpen = false;
      if (documentUiState.headerOpen) documentUi.headerOpen = false;
      return documentUi;
    }
    case HeaderType.Default:
    case HeaderType.FirstPage:
    case HeaderType.Even: {
      if (!documentUiState.headerOpen) documentUi.headerOpen = true;
      if (documentUiState.footerOpen) documentUi.footerOpen = false;
      return documentUi;
    }
    case FooterType.Default:
    case FooterType.Even:
    case FooterType.FirstPage: {
      if (!documentUiState.footerOpen) documentUi.footerOpen = true;
      if (documentUiState.headerOpen) documentUi.headerOpen = false;
      return documentUi;
    }
  }
}

function createFontProperties(
  consistentFormats: ReturnType<typeof Queries.consistentFormats>
): FontProperties {
  if (!consistentFormats) {
    return {
      bold: false,
      fontColor: '',
      fontHighlight: null,
      fontSize: '',
      fontType: '',
      italic: false,
      smallCaps: false,
      strikethrough: false,
      subscript: false,
      superscript: false,
      underline: false,
    };
  }

  const {
    bold,
    fontColor,
    fontSize,
    fontType,
    highlight,
    italic,
    smallCaps,
    strikethrough,
    suggestedEdits,
    underline,
    verticalAlign,
  } = consistentFormats;
  const activeVerticalAlign = Queries.activeVerticalAlign({ suggestedEdits, verticalAlign });

  return {
    bold: bold ? Queries.isBoldActive({ bold, suggestedEdits }) : false,
    fontColor: fontColor ? Queries.renderedFontColor({ fontColor, suggestedEdits }) : '',
    fontHighlight: highlight ? Queries.renderedHighlightColor({ highlight, suggestedEdits }) : null,
    fontSize: fontSize ? Queries.activeFontSizePt({ fontSize, suggestedEdits }).toString() : '',
    fontType: fontType ? Queries.activeFontType({ fontType, suggestedEdits }) : '',
    italic: italic ? Queries.isItalicActive({ italic, suggestedEdits }) : false,
    // A pending suggestion leaves no smallCaps value in consistentFormats, only the edit
    smallCaps: Queries.isSmallCapsActive({ smallCaps, suggestedEdits }),
    strikethrough: strikethrough
      ? Queries.isStrikethroughActive({ strikethrough, suggestedEdits })
      : false,
    subscript: activeVerticalAlign === 'subscript',
    superscript: activeVerticalAlign === 'superscript',
    underline: underline ? Queries.isUnderlineActive({ suggestedEdits, underline }) : false,
  };
}
